import React, { useEffect } from 'react';
import GiftCardBalance from './balance';
import { Box, Center, VStack, HStack, useDisclose, Skeleton } from 'native-base';
import CardHolder from './Card/CardHolder';
import ActionSheetContent from './ActionSheetContent';
import {
    GIFTCARD_FLOWS,
    observer,
    useSpringBigStore,
    useGetGiftCardList,
    GiftCard,
    useGetMember,
    useI18n,
    useDeleteGiftCards,
    useAppSettings,
    useTheme,
} from '@springbig/shared';
import NoCards from './Card/NoCards';
import PaginationDot from 'react-native-animated-pagination-dot';
import { Caption, H3 } from '~/shared/Typography';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination, Keyboard } from 'swiper';
import { SpIcon } from '~/shared/Icon';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import DeleteCardsModal from './DeleteCardsModal';
import { useToast } from '~/hooks/toast';
import EmailVerificationForm from '~/shared/EmailVerification/EmailVerificationForm';
import { isAfter, addDays } from 'date-fns';
import { SkeletonCard } from './Card';
import BowSvg from './assets/BowSvg';
import { ProfileCompleteName } from '../profile/components/ProfileCompleteName';
import { ConfirmationModal } from './ConfirmationModal';
import { Section, Button, ActionSheet, Card } from '~/shared/Theme';

const SpringbigPayScreen = () => {

    const [activeSlide, setActiveSlide] = React.useState(0);
    const [currentActionSheetItem, setCurrentActionSheetItem] = React.useState<GIFTCARD_FLOWS>(GIFTCARD_FLOWS.null);
    const [currentFlow, setCurrentFlow] = React.useState<GIFTCARD_FLOWS[]>([]);
    const [passedData, setPassedData] = React.useState<any>(null);
    const [manageGiftCard, setManageGiftCard] = React.useState<GiftCard | null>(null);
    const [hasAccess, setAccess] = React.useState<boolean>(false);
    const [isDeleteCardsModal, setIsDeleteCardsModal] = React.useState<boolean>(false);
    const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] = React.useState<boolean>(false);
    const [isDismissedEmailPrompt, setIsDismissedEmailPrompt] = React.useState<boolean>(false);
    const [isSetNameModalOpen, setIsSetNameModalOpen] = React.useState<boolean>(false);
    const [onSubmitAction, setOnSubmitAction] = React.useState<GIFTCARD_FLOWS | undefined>(undefined);
    const [isCompletedModalOpen, setIsCompletedModalOpen] = React.useState<boolean>(false);
    const [giftCardValue, setGiftCardValue] = React.useState<number>(0);

    const { primary } = useTheme();

    const {
        giftCards,
        member,
        merchant,
        walletId,
        receivedGiftCard,
        setGiftCardPresetAmounts
    } = useSpringBigStore();
    const { t } = useI18n();
    const { successToast } = useToast();

    const {
        isOpen,
        onOpen,
        onClose
    } = useDisclose();

    const { refetch: refreshMember } = useGetMember();
    const { refetch: giftCardListQuery, isLoading: isGiftCardLoading } = useGetGiftCardList();
    const { mutate: deleteGiftCards, isLoading } = useDeleteGiftCards();

    const onDeleteGiftCards = (): void => {

        const payload = member && {
            member_id: member?.id.toString(),
        };

        deleteGiftCards(payload, {
            onSuccess() {

                successToast('Cards successfully deleted');
                setIsDeleteCardsModal(false);
                springbigPayRefresh();
                refreshMember();
            }
        });
    };

    const springbigPayRefresh = () => {

        giftCardListQuery();
    };

    useEffect(() => {

        if (member) {
            springbigPayRefresh();

            const _flow: GIFTCARD_FLOWS[] = [];


            observer.on('on-set-primary-gift-card', () => {

                giftCardListQuery();
                setActiveSlide(0);
            });

            observer.on('close-actionsheet', () => {

                giftCardListQuery();
                refreshMember();
                onClose();
                _flow.length = 0;
                setCurrentActionSheetItem(GIFTCARD_FLOWS.null);
                setCurrentFlow([]);
            });

            observer.on('open-actionsheet', (flow: GIFTCARD_FLOWS) => {

                onOpen();
                setCurrentActionSheetItem(flow);
                _flow.push(flow);
                setCurrentFlow([flow]);
            });

            observer.on('change-screen', (flow: GIFTCARD_FLOWS) => {

                setCurrentActionSheetItem(flow);
                _flow.push(flow);
                setCurrentFlow(_flow);
            });

            observer.on('sent-giftcard', ({ email, amount }) => {
                setPassedData({ email, amount });
                setCurrentActionSheetItem(GIFTCARD_FLOWS.sentGift);
                setCurrentFlow([GIFTCARD_FLOWS.sentGift]);
            });

            observer.on('giftcard-back', () => {

                _flow.pop();
                setCurrentActionSheetItem(_flow[_flow.length - 1]);
                setCurrentFlow(_flow);

            });

            observer.on('on-manage-cash', (giftCard: GiftCard) => {

                setManageGiftCard(giftCard);
            });

            return () => {

                observer.off('change-screen');
                observer.off('open-actionsheet');
                observer.off('close-actionsheet');
                observer.off('giftcard-back');
                observer.off('on-set-primary-gift-card');
            };
        }
    }, [member]);

    const sortedGiftCard = giftCards.sort((a, b) => (a.primary_card === b.primary_card ? 0 : a.primary_card ? -1 : 1));

    useEffect(() => {

        if (!member) {
            return;
        }

        setTimeout(() => {

            setAccess(!!member.gift_card_enabled);
        }, 1000);
    }, [member]);

    const { requireEmailVerficationGiftCard, giftCardPresetAmounts } = useAppSettings();

    useEffect(() => {

        if (member) {
            setIsEmailVerificationModalOpen(
                !member.consumer.email_validated && (
                    !localStorage.getItem('last_shown_email_verification_prompt')
                    ||
                    isAfter(
                        new Date(),
                        addDays(new Date(
                            JSON.parse(
                                localStorage.getItem('last_shown_email_verification_prompt')
                            )
                        ), 4)
                    )
                )
            );
        }

        setGiftCardPresetAmounts(giftCardPresetAmounts);

        if (!requireEmailVerficationGiftCard) {

            setIsEmailVerificationModalOpen(false);
        }
    }, [member, requireEmailVerficationGiftCard, setGiftCardPresetAmounts, giftCardPresetAmounts]);

    const onCancelEMailPrompt = (): void => {

        localStorage.setItem(
            'last_shown_email_verification_prompt',
            JSON.stringify(new Date())
        );
        setIsDismissedEmailPrompt(true);
    };

    if (isGiftCardLoading || (!member && !hasAccess) || !hasAccess) {
        return <SpringbigPaySkeleton />;
    }

    return (
        <Section px={4}>
            {
                !merchant?.hide_gift_card_payments &&
                <VStack justifyContent={'center'} alignItems={'center'} p={4} bg={'#F48309'} mx={-4}>
                    <Caption color={'white'}>{ merchant?.gift_card_alias } purchases or reloads are temporarily unavailable.</Caption>
                </VStack>
            }
            { hasAccess ?
                <>
                    <ConfirmationModal
                        isOpen={isCompletedModalOpen}
                        onClose={() => setIsCompletedModalOpen(false)}
                        giftCardValue={giftCardValue}
                    />
                    <ProfileCompleteName
                        isOpen={isSetNameModalOpen}
                        onClose={() => setIsSetNameModalOpen(false)}
                        t={t}
                        type='pay'
                    />
                    <ActionSheet
                        isOpen={isEmailVerificationModalOpen && !isDismissedEmailPrompt}
                        hideDragIndicator
                    >
                        <VStack space={4} w={'100%'}>
                            <H3
                                text={'giftCard.emailVerificationTitle'}
                                textAlign={'center'}
                            />
                            <EmailVerificationForm
                                t={t}
                                walletId={walletId}
                                onCancel={onCancelEMailPrompt}
                                onProceed={onCancelEMailPrompt}
                            />
                        </VStack>
                    </ActionSheet>

                    <GiftCardBalance setOnSubmitAction={setOnSubmitAction}  setIsSetNameModalOpen={setIsSetNameModalOpen} />

                    { receivedGiftCard &&
                        <HStack justifyContent={'center'} alignItems={'center'} space={2} py={2}>
                            <BowSvg fill={primary} width={30} height={30} />
                            <Caption
                                text={'giftCard.received.disclaimer'}
                                textValues={[merchant.gift_card_alias]}
                            />
                        </HStack>
                    }
                    <Card hasShadow>
                        <Swiper
                            spaceBetween={0}
                            navigation={false}
                            keyboard={true}
                            modules={[Pagination, Mousewheel, Keyboard]}
                            className="mySwiper"
                            style={{
                                pointerEvents: 'auto',
                                width: '100%',
                            }}
                            onSlideChange={(swiper) => {

                                setActiveSlide(swiper.snapIndex);
                            }}
                        >
                            { (sortedGiftCard && sortedGiftCard.length > 0) ?
                                sortedGiftCard.map((card, index) => (

                                    <SwiperSlide
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                        }}
                                        key={index}
                                    >
                                        <CardHolder
                                            cardContent={card}
                                            openNameModal={() => setIsSetNameModalOpen(true)}
                                            setOnSubmitAction={setOnSubmitAction}
                                        />
                                    </SwiperSlide>
                                ))
                                :
                                <SwiperSlide>
                                    <NoCards
                                        setIsSetNameModalOpen={setIsSetNameModalOpen}
                                        setOnSubmitAction={setOnSubmitAction}
                                    />
                                </SwiperSlide>
                            }

                        </Swiper>
                    </Card>
                    <Center mt={3}>
                        <PaginationDot
                            activeDotColor={primary}
                            curPage={activeSlide}
                            maxPage={(giftCards ? giftCards.length : 1)}
                        />
                    </Center>
                    <VStack alignItems={'center'} pt={2}>
                        { giftCards.length > 0 && giftCards.some(giftCard => giftCard.balance === '$0.00') &&
                            <Button
                                variant={'text'}
                                modifier={'unlocked'}
                                onPress={() => setIsDeleteCardsModal(true)}
                            >
                                <SpIcon size={12} icon={faTrash} color={primary} />
                            </Button>
                        }
                        <HStack justifyContent={'center'} flexWrap={'wrap'}>
                            {
                                merchant &&
                                !merchant.hide_gift_card_payments &&
                                !merchant.gift_cards_purchase_disabled &&
                                <Button
                                    text={'giftCard.sendToFriend'}
                                    textValues={[merchant?.gift_card_alias]}
                                    variant={'text'}
                                    onPress={() => {

                                        if (!member?.first_name || !member?.last_name || !member.confirmed_identity) {
                                            setIsSetNameModalOpen(true);
                                            setOnSubmitAction(GIFTCARD_FLOWS.sendGiftCard);
                                        }
                                        else {
                                            observer.emit('open-actionsheet', GIFTCARD_FLOWS.sendGiftCard);
                                        }
                                    }}
                                />
                            }
                            <Button
                                text={'giftCard.importExistingButton'}
                                textValues={[merchant?.gift_card_alias]}
                                variant={'text'}
                                onPress={() => {

                                    if (!member?.first_name || !member?.last_name || !member.confirmed_identity) {
                                        setIsSetNameModalOpen(true);
                                        setOnSubmitAction(GIFTCARD_FLOWS.existingGiftCard);
                                    }
                                    else {
                                        observer.emit('open-actionsheet', GIFTCARD_FLOWS.existingGiftCard);
                                    }
                                }}
                            />
                        </HStack>
                    </VStack>
                    { isDeleteCardsModal &&
                        <DeleteCardsModal
                            onCancel={() => setIsDeleteCardsModal(false)}
                            onSubmit={onDeleteGiftCards}
                            isLoading={isLoading}
                        />
                    }
                    <ActionSheetContent
                        currentActionSheetItem={currentActionSheetItem}
                        isOpen={isOpen}
                        currentFlow={currentFlow}
                        data={passedData}
                        manageGiftCard={manageGiftCard}
                        setGiftCardValue={setGiftCardValue}
                        setIsCompletedModalOpen={setIsCompletedModalOpen}
                    />
                </>
                :
                <Box alignItems={'center'} justifyContent={'center'} h={'100%'}>
                    <Caption text={'giftCard.no_access'} />
                </Box>
            }
        </Section>
    );
};

const SpringbigPaySkeleton = () => {

    const {
        primary,
        card,
        radius_sm,
        radius_md,
    } = useTheme();

    return (
        <VStack alignItems={'center'} p={4} space={4}>
            <HStack>
                <VStack alignItems={'center'} space={2}>
                    <Skeleton h={10} w={'8rem'} rounded={radius_sm} />
                    <Skeleton h={'1.7rem'} w={'8rem'} rounded={radius_sm} />
                    <Skeleton.Text lines={2} w={'6rem'} />
                </VStack>
                <Box
                    borderLeftColor={primary}
                    borderLeftWidth={1}
                    h='70%'
                    alignSelf={'center'}
                    mx={'2rem'}
                />
                <VStack alignItems={'center'} space={4}>
                    <Skeleton h={10} w={'8rem'} rounded={radius_sm} />
                    <Skeleton.Text lines={2} w={'6rem'} />
                </VStack>
            </HStack>
            <VStack
                rounded={radius_md}
                backgroundColor={card}
                w={'full'}
                space={4}
                p={4}
            >
                <SkeletonCard />
            </VStack>
            <Skeleton
                h={4}
                w={'6rem'}
                rounded={radius_sm}
                alignSelf={'center'}
            />
        </VStack>
    );
};

export default SpringbigPayScreen;
